import { Icon } from '@pelando/components';
import { useRouter } from 'next/router';
import { useTranslation } from '@/presentation/hooks/useTranslation';
import { Ghost } from '@pelando/fontawesome/icons';
import { type ProfileComment } from '@/domain/entities/Comment';
import { type VoteVariables } from '@/infra/api/votes/types/Vote';
import { type RemoveVoteVariables } from '@/infra/api/votes/types/RemoveVote';
import CommentsListSkeleton from './skeleton';
import { TabsLoadMoreButton } from '../DealsTabs/style';
import { EmptyContainer } from './style';
import { ProfileCommentCard } from '../ProfileCommentCard';

export type CommentsListProps = {
  isOwner?: boolean;
  isLoading?: boolean;
  comments: ProfileComment[];
  userNickname: string;
  isVerified: boolean;
  userImage?: string;
  hasNextPage?: boolean;
  onLoadMore: () => void;
  onVote: (variables: VoteVariables) => void;
  onRemoveVote: (variables: RemoveVoteVariables) => void;
};

function CommentsList({
  isOwner,
  isLoading,
  comments,
  userNickname,
  isVerified,
  userImage,
  hasNextPage,
  onLoadMore,
  onVote,
  onRemoveVote,
}: CommentsListProps) {
  const { t } = useTranslation('profile', 'common');
  const { push } = useRouter();

  if (!comments.length && !isLoading) {
    const emptyStateProps = {
      icon: <Icon icon={Ghost} />,
      title: t('comment-list-empty-state-title'),
      content: isOwner
        ? t('comment-list-empty-state-subtitle-is-owner')
        : `${userNickname} ${t('comment-list-empty-state-subtitle')}`,
      buttonTitle: isOwner ? t('comment-list-empty-state-button') : '',
      onClick: isOwner ? () => push('/') : undefined,
    };

    return <EmptyContainer {...emptyStateProps} />;
  }

  const loadMoreHandler = () => {
    onLoadMore();
  };

  const showLoadMoreButton = comments.length > 0 && !isLoading && hasNextPage;

  return (
    <>
      {comments.map((comment, index) => (
        <ProfileCommentCard
          key={comment.id}
          comment={comment}
          onVote={onVote}
          onRemoveVote={onRemoveVote}
          cardPosition={index}
          isVerified={isVerified}
          userNickname={userNickname}
          userImage={userImage}
        />
      ))}
      {showLoadMoreButton && (
        <TabsLoadMoreButton onLoadMore={loadMoreHandler} />
      )}
      {isLoading && <CommentsListSkeleton />}
    </>
  );
}

export default CommentsList;
